import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from "react";

export interface ChartPoint {
  /** Short x-axis label, e.g. "Oct 3". */
  label: string;
  value: number;
}

const HEIGHT = 220;
const PAD = { top: 12, right: 12, bottom: 24, left: 52 };

/** Rounds a step up to 1, 2, 2.5 or 5 times a power of ten so ticks land on round numbers. */
function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const f = raw / pow;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * pow;
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => entry && setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * Single-series bar chart drawn in SVG, one bar per point (a day): days without volume show no
 * bar instead of a line dipping to zero. Recessive grid, the hovered bar highlighted with a
 * tooltip. Screen readers get the same data as a hidden table.
 */
export function BarChart({
  points,
  label,
  formatTick,
  tooltip,
}: {
  points: ChartPoint[];
  /** Accessible name and caption of the data table. */
  label: string;
  formatTick: (value: number) => string;
  /** Tooltip body for point `i`. */
  tooltip: (i: number) => ReactNode;
}) {
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);

  const n = points.length;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const step = niceStep(Math.max(...points.map((p) => p.value), 0) / 4);
  const yMax = step * 4;
  // Each point gets an equal band; its bar is centred in it, with a gap between bars.
  const band = n > 0 ? plotW / n : 0;
  const barW = Math.max(2, Math.min(24, band * 0.62));
  const x = (i: number) => PAD.left + band * (i + 0.5);
  const y = (v: number) => PAD.top + plotH - (v / yMax) * plotH;
  const ticks = [0, 1, 2, 3, 4].map((k) => k * step);
  // About five evenly spaced x labels, always including both ends.
  const every = Math.max(1, Math.ceil((n - 1) / 4));
  const xLabels = points.map((_, i) => i).filter((i) => i % every === 0 || i === n - 1);
  const xLabelsSpaced = xLabels.filter((i, k) => k === xLabels.length - 1 || n - 1 - i >= every / 2);

  function onPointerMove(e: PointerEvent<SVGRectElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.floor(((e.clientX - rect.left) / rect.width) * n);
    setActive(Math.min(n - 1, Math.max(0, i)));
  }

  return (
    <div ref={wrapRef} className="relative" style={{ height: HEIGHT }}>
      {width > 0 && n > 0 && (
        <svg width={width} height={HEIGHT} aria-hidden="true" className="block overflow-visible">
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--line)"
                strokeDasharray={t === 0 ? undefined : "2 3"}
              />
              <text
                x={PAD.left - 8}
                y={y(t)}
                dy="0.32em"
                textAnchor="end"
                className="fill-fg-subtle text-[11px] tabular-nums"
              >
                {formatTick(t)}
              </text>
            </g>
          ))}
          {xLabelsSpaced.map((i) => (
            <text key={i} x={x(i)} y={HEIGHT - 6} textAnchor="middle" className="fill-fg-subtle text-[11px]">
              {points[i]?.label}
            </text>
          ))}
          {active !== null && (
            <rect x={PAD.left + band * active} y={PAD.top} width={band} height={plotH} fill="var(--subtle)" />
          )}
          {points.map((p, i) =>
            p.value > 0 ? (
              <rect
                key={p.label + String(i)}
                x={x(i) - barW / 2}
                y={y(p.value)}
                width={barW}
                // At least 2px, so a small day is still visible next to a big one.
                height={Math.max(2, y(0) - y(p.value))}
                rx={Math.min(3, barW / 3)}
                fill="var(--brand)"
                fillOpacity={active === null || active === i ? 0.9 : 0.45}
              />
            ) : null,
          )}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={plotW}
            height={plotH}
            fill="transparent"
            onPointerMove={onPointerMove}
            onPointerLeave={() => setActive(null)}
          />
        </svg>
      )}
      {active !== null && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 z-10 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs whitespace-nowrap shadow-md"
          style={x(active) > width / 2 ? { right: width - x(active) + 10 } : { left: x(active) + 10 }}
        >
          {tooltip(active)}
        </div>
      )}
      {/* sr-only on the wrapper: a <table> ignores the 1px height and would stretch the page. */}
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <tbody>
            {points.map((p, i) => (
              <tr key={p.label + String(i)}>
                <td>{tooltip(i)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
